import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { formatUsd } from '../lib/format';

export type PaymentMoney = {
  expectedCents: number;
  receivedCents: number;
  acvCents: number | null;
  depreciationCents: number | null;
  deductibleCents: number | null;
};

/**
 * Barra de progreso de PAGOS animada + resumen legible: cuánto se ha recibido, cuánto falta por
 * llegar y el desglose de lo esperado (ACV / Depreciation / Deductible). La barra se LLENA al
 * montar (animación). La usan el tab Payments del claim completo y la tarjeta lateral del claim
 * (ClaimPaymentsFull, donde está el tracker de invoices). `compact` = versión chica para esa
 * tarjeta de la derecha. Todo bilingüe vía i18n y con tokens del tema (dark mode).
 */
export function PaymentProgressBar({ money, compact = false }: { money: PaymentMoney | null; compact?: boolean }) {
  const { t } = useI18n();
  const [fill, setFill] = useState(0);

  const expected = money?.expectedCents ?? 0;
  const received = money?.receivedCents ?? 0;
  const pct = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;
  const pending = Math.max(0, expected - received);
  const done = expected > 0 && pending < 100; // < $1 de diferencia => pagado

  // La barra arranca en 0 y se llena a `pct` al montar/cambiar (transición CSS).
  useEffect(() => {
    const id = setTimeout(() => setFill(pct), 80);
    return () => clearTimeout(id);
  }, [pct]);

  // Se muestra SIEMPRE (pedido del dueño: todos los claims con su resumen, aunque sea $0). Sin
  // estimado aprobado (expected = 0) la barra va vacía y se avisa que falta cargar el estimado.
  if (!money) return null;
  const noExpected = expected <= 0;

  const barH = compact ? 12 : 16;
  const green = 'var(--green, #16a34a)';
  const accent = 'var(--accent, #2563eb)';

  const chips: Array<{ label: string; value: number; color: string }> = [];
  if (money.acvCents != null) chips.push({ label: 'ACV', value: money.acvCents, color: accent });
  if (money.depreciationCents != null) chips.push({ label: t('pb.depreciation'), value: money.depreciationCents, color: '#8b5cf6' });
  if (money.deductibleCents != null) chips.push({ label: t('pb.deductible'), value: money.deductibleCents, color: 'var(--muted)' });

  return (
    <div style={{ display: 'grid', gap: compact ? 7 : 10 }}>
      {/* Encabezado: recibido / esperado + pastilla de estado */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: compact ? 'var(--fs-md)' : 'var(--fs-lg)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], color: green, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', letterSpacing: -0.4 }}>{formatUsd(received)}</span>
          <span className="muted" style={{ fontSize: compact ? 'var(--fs-2xs)' : 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }}>/ {formatUsd(expected)} {t('pb.expected')}</span>
        </div>
        <span
          className="badge"
          style={{
            fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], padding: '3px 9px',
            background: done ? 'var(--green-soft, #dcfce7)' : 'var(--surface-2, #eef2ff)',
            color: done ? green : accent,
          }}
        >
          {noExpected ? t('pb.noEstimate') : done ? <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pb.fullyPaid')}</> : `${pct}% ${t('pb.collected')}`}
        </span>
      </div>

      {/* Barra animada: se llena de izquierda a derecha; verde si está pago, degradado si en curso */}
      <div style={{ position: 'relative', height: barH, borderRadius: 999, background: 'var(--surface-2, #eef1f4)', overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)' }}>
        <div
          style={{
            width: `${fill}%`, height: '100%', borderRadius: 999,
            background: done ? green : `linear-gradient(90deg, ${accent}, ${green})`,
            transition: 'width 1.1s cubic-bezier(.22,1,.36,1)',
          }}
        />
        {/* marcador del % centrado cuando no es compacto */}
        {!compact && pct >= 8 && (
          <span style={{ position: 'absolute', top: 0, left: 0, width: `${fill}%`, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8, fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], color: '#fff', transition: 'width 1.1s cubic-bezier(.22,1,.36,1)', textShadow: '0 1px 1px rgba(0,0,0,0.25)' }}>{pct}%</span>
        )}
      </div>

      {/* Línea legible: qué falta por llegar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: compact ? 'var(--fs-2xs)' : 'var(--fs-xs)', flexWrap: 'wrap' }}>
        {noExpected ? (
          <span className="muted">{t('pb.noEstimateNote')}</span>
        ) : done ? (
          <span style={{ color: green, fontWeight: 700 }}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pb.allCollected')}</span>
        ) : (
          <span className="muted">
            {t('pb.remaining')}: <strong style={{ color: 'var(--ink, #111)', fontVariantNumeric: 'tabular-nums' }}>{formatUsd(pending)}</strong> {t('pb.stillComing')}
          </span>
        )}
      </div>

      {/* Desglose de lo esperado (ACV / Depreciation / Deductible) con puntos de color */}
      {!compact && chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2 }}>
          {chips.map((c) => (
            <span key={c.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-2xs)', padding: '4px 9px', borderRadius: 8, background: 'var(--surface-2, #f6f8fa)' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: c.color, flex: '0 0 auto' }} />
              <span className="muted">{c.label}</span>
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatUsd(c.value)}</strong>
              {c.label === t('pb.deductible') && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>({t('pb.deductibleNote')})</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
